import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import DataTable, { TableStyles } from 'react-data-table-component';
import Select from 'react-select';
import moment from 'moment';
import { encodeId } from '@app/helpers/base64url';
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import { Button } from '@app/components';
import { getAllActiveModulesBySubject } from '@app/services/module';
import {
  getStudentAutoMarkingList,
  StudentAutoMarkingItem,
  listBatchUploads,
  BatchUploadSummary,
} from '@app/services/studentAutoMarking';
import { getStudentById, getStudentSubjectsByStatusActive } from '@app/services/student';

import './StudentAutoMarking.css';

const tableStyles: TableStyles = {
  head: {
    style: { color: '#5E5873', fontSize: '13px', fontWeight: 900 },
  },
  headCells: {
    style: { borderColor: '#E9ECEF', textTransform: 'uppercase', letterSpacing: '1px' },
  },
  headRow: {
    style: { backgroundColor: '#F7F7F8', minHeight: '44px', fontWeight: 500 },
  },
};

const ListStudentAutoMarking = () => {
  const isMounted = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Guard in App.tsx validates studentId before this mounts — just decode the value
  const studentIdFromUrl = useDecodedParam('studentId', '/students');
  const hasStudentParam  = !!window.location.pathname.match(/\/student\/[^/]+\/auto-marking/);
  const fixedStudentId   = hasStudentParam ? studentIdFromUrl : null;
  const canAccess = useSelector((state: any) => state.auth.permissions?.['student-auto-marking']);

  // Persist page/perPage in URL — same as ListStudents
  const [perPage, setPerPage]         = useState<number>(Number(searchParams.get('perPage') ?? 10));
  const [currentPage, setCurrentPage] = useState<number>(Number(searchParams.get('page') ?? 1));
  const [sortField, setSortField]     = useState(searchParams.get('sortField') ?? '');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortDir') ?? '');

  const [data, setData]           = useState<StudentAutoMarkingItem[]>([]);
  const [isLoading, setLoading]   = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [subjects, setSubjects]           = useState<any[]>([]);
  const [fixedStudentName, setFixedStudentName] = useState<string>('');

  // Filters — also persisted in URL
  const [search, setSearch] = useState({
    subjectId:       searchParams.get('subjectId')       ?? '',
    moduleId:        searchParams.get('moduleId')        ?? '',
    worksheetNumber: searchParams.get('worksheetNumber') ?? '',
  });

  const [modules, setModules]       = useState<any[]>([]);
  const [worksheets, setWorksheets] = useState<any[]>([]);

  // Batch upload progress tracking
  const [activeBatches, setActiveBatches] = useState<BatchUploadSummary[]>([]);
  const [isLoadingBatch, setLoadingBatch] = useState(false);
  const batchPollTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (batchPollTimerRef.current) clearTimeout(batchPollTimerRef.current);
    };
  }, []);

  // Load student info when fixedStudentId resolves
  useEffect(() => {
    if (fixedStudentId) {
      loadStudentInfo(fixedStudentId);
      fetchActiveBatch(fixedStudentId);
      
      // Restore filter dropdowns from URL params on page load
      const subjectId = searchParams.get('subjectId');
      const moduleId = searchParams.get('moduleId');
      
      if (subjectId) {
        // Load modules for the selected subject
        getAllActiveModulesBySubject(Number(subjectId)).then(resp => {
          if (resp?.success && isMounted.current) {
            const modulesList = resp.collections || [];
            setModules(modulesList);
            
            // If moduleId is also in URL, generate worksheets for that module
            if (moduleId) {
              const selectedModule = modulesList.find(m => String(m.id) === moduleId);
              if (selectedModule && selectedModule.worksheet_count) {
                const worksheetList = Array.from(
                  { length: selectedModule.worksheet_count },
                  (_, i) => ({ worksheet_number: i + 1 })
                );
                setWorksheets(worksheetList);
              }
            }
          }
        }).catch(() => { /* silent */ });
      }
    }
  }, [fixedStudentId]);

  // Poll active batches every 5 seconds if any exist and are not terminal
  useEffect(() => {
    if (activeBatches.length > 0 && fixedStudentId) {
      const hasNonTerminal = activeBatches.some(batch => 
        !['completed', 'completed_with_errors', 'failed'].includes(batch.status)
      );
      if (hasNonTerminal) {
        batchPollTimerRef.current = setTimeout(() => {
          fetchActiveBatch(fixedStudentId);
        }, 5000);
      }
    }
    return () => {
      if (batchPollTimerRef.current) clearTimeout(batchPollTimerRef.current);
    };
  }, [activeBatches, fixedStudentId]);

  // Refetch whenever searchParams change (page, perPage, filters)
  useEffect(() => {
    fetchList(
      Number(searchParams.get('page') ?? 1),
      Number(searchParams.get('perPage') ?? 10),
      searchParams.get('sortField') ?? '',
      searchParams.get('sortDir') ?? '',
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, fixedStudentId]);

  const loadStudentInfo = async (sid: number) => {
    try {
      const [studentResp, subjectsResp] = await Promise.all([
        getStudentById(sid),
        getStudentSubjectsByStatusActive(sid),
      ]);
      if (!isMounted.current) return;
      if (studentResp?.success) {
        const s = studentResp.collections;
        setFixedStudentName([s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ').trim() || `Student #${sid}`);
      }
      if (subjectsResp?.success) setSubjects(subjectsResp.collections || []);
    } catch { /* silent */ }
  };

  const fetchActiveBatch = async (studentId: number) => {
    try {
      setLoadingBatch(true);
      const resp = await listBatchUploads(studentId);
      if (!isMounted.current) return;
      
      if (resp?.success && resp?.collections) {
        // Get all non-terminal batches
        const batches = resp.collections as BatchUploadSummary[];
        const nonTerminalBatches = batches.filter((b: BatchUploadSummary) => 
          !['completed', 'completed_with_errors', 'failed'].includes(b.status)
        );
        setActiveBatches(nonTerminalBatches);
      }
    } catch { /* silent */ } finally {
      if (isMounted.current) setLoadingBatch(false);
    }
  };

  const fetchList = async (page: number, pageSize: number, field: string, direction: string) => {
    if (!fixedStudentId) return;
    try {
      setLoading(true);
      const subjectId       = searchParams.get('subjectId')       ?? '';
      const moduleId        = searchParams.get('moduleId')        ?? '';
      const worksheetNumber = searchParams.get('worksheetNumber') ?? '';
      const resp = await getStudentAutoMarkingList({
        studentId:       String(fixedStudentId),
        subjectId:       subjectId || undefined,
        moduleId:        moduleId || undefined,
        worksheetNumber: worksheetNumber || undefined,
        page,
        perPage:         pageSize,
        sortField:       field || undefined,
        sortDirection:   direction || undefined,
      });
      if (!isMounted.current) return;
      if (resp?.success) {
        // API returns: { success, collections: [...], pagination: {...} }
        // Collections is always an array, pagination is at root level
        setData(resp.collections || []);
        setTotalRows(resp?.pagination?.total || 0);
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to load data');
      }
    } catch (err: any) {
      if (isMounted.current) toast.error(err?.message || 'Internal Server Error');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    searchParams.set('page', String(page));
    setSearchParams(searchParams);
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    searchParams.set('perPage', String(newPerPage));
    searchParams.set('page', String(page));
    setSearchParams(searchParams);
    setPerPage(newPerPage);
  };

  const handleSort = (column: any, dir: string) => {
    searchParams.set('sortField', column.sortField);
    searchParams.set('sortDir', dir);
    setSearchParams(searchParams);
    setSortField(column.sortField);
    setSortDirection(dir);
  };

  const applyFilter = (key: string, value: string) => {
    const updated = { ...search, [key]: value };

    // Reset dependent dropdowns
    if (key === 'subjectId') {
      updated.moduleId = '';
      updated.worksheetNumber = '';
      searchParams.delete('moduleId');
      searchParams.delete('worksheetNumber');
      setModules([]);
      setWorksheets([]);
      if (value) {
        // Fetch active modules for the selected subject
        getAllActiveModulesBySubject(Number(value)).then(resp => {
          if (resp?.success && isMounted.current) {
            setModules(resp.collections || []);
          }
        }).catch(() => { /* silent */ });
      }
    }

    if (key === 'moduleId') {
      updated.worksheetNumber = '';
      searchParams.delete('worksheetNumber');
      setWorksheets([]);
      if (value) {
        // Find selected module and generate worksheet numbers from worksheet_count
        const selectedModule = modules.find(m => String(m.id) === value);
        if (selectedModule && selectedModule.worksheet_count) {
          const worksheetList = Array.from(
            { length: selectedModule.worksheet_count },
            (_, i) => ({ worksheet_number: i + 1 })
          );
          setWorksheets(worksheetList);
        }
      }
    }

    setSearch(updated);
    searchParams.set('page', '1');
    if (updated.subjectId)       searchParams.set('subjectId', updated.subjectId);
    else                          searchParams.delete('subjectId');
    if (updated.moduleId)        searchParams.set('moduleId', updated.moduleId);
    else                          searchParams.delete('moduleId');
    if (updated.worksheetNumber) searchParams.set('worksheetNumber', updated.worksheetNumber);
    else                          searchParams.delete('worksheetNumber');
    setSearchParams(searchParams);
  };

  const handleReset = () => {
    setSearch({ subjectId: '', moduleId: '', worksheetNumber: '' });
    setModules([]);
    setWorksheets([]);
    searchParams.delete('subjectId');
    searchParams.delete('moduleId');
    searchParams.delete('worksheetNumber');
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const uploadLink = fixedStudentId
    ? `/student/${encodeId(fixedStudentId)}/auto-marking/upload`
    : '/students';

  const columns = [
    {
      name: 'Subject',
      selector: (row: StudentAutoMarkingItem) => row.subject_name,
      sortField: 'subject_id',
      sortable: true,
      wrap: true,
      cell: (row: StudentAutoMarkingItem) =>
        (row.subject_name || '').replace(/\b\w/g, c => c.toUpperCase()),
    },
    {
      name: 'Module / Worksheet',
      sortField: 'module_id',
      sortable: true,
      // Format: "<Module> - <Worksheet>", e.g. "DU3a - 45"
      cell: (row: StudentAutoMarkingItem) =>
        row.module_name || row.worksheet_number ? (
          <span style={{ fontWeight: 600 }}>
            {[row.module_name, row.worksheet_number].filter(Boolean).join(' - ')}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      name: 'Results',
      cell: (row: StudentAutoMarkingItem) => {
        const s = row.summary;
        if (!s) {
          const total = row.total_marks;
          const max   = row.max_marks;
          const pct   = row.percentage;
          if (total !== null && total !== undefined) {
            return <span>{total} / {max}{pct ? ` (${pct}%)` : ''}</span>;
          }
          return <span className="text-muted">—</span>;
        }
        return (
          <div style={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700 }}>{s.totalMarks} / {s.maxMarks} <span style={{ color: '#6b7280', fontWeight: 400 }}>({s.percentage}%)</span></div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ color: '#10b981' }}>✓{s.correctAnswers}</span>
              <span style={{ color: '#f59e0b' }}>~{s.partiallyCorrect}</span>
              <span style={{ color: '#ef4444' }}>✗{s.incorrect}</span>
              {(s.unanswered ?? 0) > 0 && <span style={{ color: '#6b7280' }}>—{s.unanswered}</span>}
            </div>
          </div>
        );
      },
      sortField: 'total_marks',
      sortable: true,
    },
    {
      name: 'Date',
      cell: (row: StudentAutoMarkingItem) =>
        moment(row.created_at).format('DD MMM YYYY, hh:mm A'),
      sortField: 'created_at',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Action',
      cell: (row: StudentAutoMarkingItem) => (
        <>
          {canAccess?.view && fixedStudentId && (
            <Link
              to={`/student/${encodeId(fixedStudentId)}/auto-marking/${encodeId(row.id)}`}
              state={{ studentEncodedId: encodeId(fixedStudentId) }}
              data-toggle="tooltip"
              title="View Details"
            >
              <i className="fas fa-eye mr-2" />
            </Link>
          )}
        </>
      ),
    },
  ];

  if (canAccess && !canAccess.list) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning mt-3">
              <i className="fas fa-exclamation-triangle mr-2" />
              You do not have permission to access Student Auto Marking.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                {fixedStudentName
                  ? `Auto Marking — ${fixedStudentName}`
                  : 'Student Auto Marking'}
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/students">Students</Link></li>
                {fixedStudentName && fixedStudentId && (
                  <li className="breadcrumb-item">
                    <span className="text-muted">{fixedStudentName}</span>
                  </li>
                )}
                <li className="breadcrumb-item active">Auto Marking</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    Auto-Marked Worksheets
                    {fixedStudentName && (
                      <span className="badge badge-info ml-2">{fixedStudentName}</span>
                    )}
                  </h3>
                  <div className="col-lg-12 text-right">
                    <Link className="btn btn-default mr-2" to="/students">
                      <i className="fas fa-arrow-left mr-1" /> Back to Students
                    </Link>
                    {activeBatches.length > 0 && fixedStudentId && (
                      <>
                        {activeBatches.map((batch, index) => (
                          <Link
                            key={batch.id}
                            className="btn btn-info mr-2"
                            to={`/student/${encodeId(fixedStudentId)}/auto-marking/batch/${encodeId(batch.id)}`}
                          >
                            <i className="fas fa-tasks mr-1" />
                            Batch #{batch.id} ({batch.done}/{batch.total})
                            {!['completed', 'completed_with_errors', 'failed'].includes(batch.status) && (
                              <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>
                            )}
                          </Link>
                        ))}
                      </>
                    )}
                    {canAccess?.create && (
                      <Link className="btn btn-primary" to={uploadLink}>
                        <i className="fas fa-upload mr-1" /> Upload &amp; Auto-Mark
                      </Link>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  {/* Filters */}
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <Select
                        placeholder="Filter by Subject"
                        options={subjects}
                        getOptionValue={(o) => String(o.id)}
                        getOptionLabel={(o) => o.name}
                        value={subjects.find((s) => String(s.id) === search.subjectId) || null}
                        onChange={(opt) => applyFilter('subjectId', opt ? String(opt.id) : '')}
                        isClearable
                      />
                    </div>
                    <div className="col-sm-3">
                      <Select
                        placeholder={!search.subjectId ? 'Select Subject first' : 'Filter by Module'}
                        options={modules}
                        getOptionValue={(o) => String(o.id)}
                        getOptionLabel={(o) => o.name}
                        value={modules.find((m) => String(m.id) === search.moduleId) || null}
                        onChange={(opt) => applyFilter('moduleId', opt ? String(opt.id) : '')}
                        isDisabled={!search.subjectId}
                        isClearable
                      />
                    </div>
                    <div className="col-sm-3">
                      <Select
                        placeholder={!search.moduleId ? 'Select Module first' : 'Filter by Worksheet'}
                        options={worksheets.map((w) => ({
                          value: String(w.worksheet_number),
                          label: `Worksheet ${w.worksheet_number}`,
                        }))}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        value={
                          search.worksheetNumber
                            ? { value: search.worksheetNumber, label: `Worksheet ${search.worksheetNumber}` }
                            : null
                        }
                        onChange={(opt) => applyFilter('worksheetNumber', opt ? opt.value : '')}
                        isDisabled={!search.moduleId}
                        isClearable
                      />
                    </div>
                    <div className="col-sm-3 d-flex align-items-end">
                      <Button
                        type="button"
                        isLoading={isLoading}
                        onClick={handleReset}
                        className="btn btn-warning"
                      >
                        Reset Filter
                      </Button>
                    </div>
                  </div>

                  <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[5, 10, 20, 30, 50]}
                    progressPending={isLoading}
                    sortServer
                    onSort={handleSort}
                    customStyles={tableStyles}
                    noDataComponent={
                      <div className="py-4 text-center text-muted">
                        <i className="fas fa-inbox fa-2x mb-2 d-block" />
                        No auto-marked worksheets found.
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListStudentAutoMarking;
